import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';

import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import type { OperationRelationshipDto } from '../../api/models/OperationRelationshipDto';
import type { OperationDto } from '../../api/models/OperationDto';
import type { MaterialDto } from '../../api/models/MaterialDto';
import { MaterialControllerService } from '../../api/services/MaterialControllerService';
import { DurationInput } from '../../components/DurationInput';

interface OperationRelationshipDetailDialogProps {
  value?: OperationRelationshipDto | null;
  onClose: () => void;
  onSave: (relationship: OperationRelationshipDto) => void;
  availableOperations?: OperationDto[];
  availableResources?: Array<{id: string, name: string, resourceTypeId: string}>; // currently unused but kept for future use
  allRelationships?: OperationRelationshipDto[]; // All relationships in the routing (for split validation) - currently unused but kept for future use
  exemplarQuantity?: number; // Exemplar quantity for split calculation (e.g., minDoughQuantity) - currently unused but kept for future use
  routingMaterialId?: string; // UUID of the routing's output material (to get initialWeighInGrams)
}

const OperationRelationshipDetailDialog: React.FC<OperationRelationshipDetailDialogProps> = ({
  value,
  onClose,
  onSave,
  availableOperations = [],
  availableResources: _availableResources = [],
  allRelationships: _allRelationships = [],
  exemplarQuantity: _exemplarQuantity,
  routingMaterialId
}) => {
  const { t } = useTranslation(['common']);
  const [, setRoutingMaterial] = useState<MaterialDto | null>(null);
  
  const [predecessorId, setPredecessorId] = useState<string>(value?.predecessorId || '');
  const [successorId, setSuccessorId] = useState<string>(value?.successorId || '');
  const [relationType, setRelationType] = useState<'START_TO_START' | 'START_TO_END' | 'END_TO_START' | 'END_TO_END'>(
    value?.relationType || 'START_TO_START'
  );
  // Always set to CONSTRAINT (as per specification - only resource constraints are needed)
  const [relationshipCategory, setRelationshipCategory] = useState<'TIME' | 'CONSTRAINT'>(
    'CONSTRAINT'
  );
  const [minDistance, setMinDistance] = useState<number>(value?.minDistance ?? 0);
  // IMPORTANT: 0 is a valid value for maxDistance, so we must check explicitly for undefined/null
  // Using || would treat 0 as falsy and set it to undefined
  const [maxDistance, setMaxDistance] = useState<number | undefined>(
    value?.maxDistance !== undefined && value?.maxDistance !== null ? value.maxDistance : undefined
  );
  const [validationErrors, setValidationErrors] = useState<{[key: string]: boolean}>({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Refs for DurationInput components to commit values
  const minDistanceRef = useRef<{ commit: () => void } | null>(null);
  const maxDistanceRef = useRef<{ commit: () => void } | null>(null);
  
  // Resource constraint state - OR-conjunction structure
  const [predecessorResourceIds, setPredecessorResourceIds] = useState<string[]>([]);
  const [successorResourceIds, setSuccessorResourceIds] = useState<string[]>([]);
  const [constraintType, setConstraintType] = useState<'SameResource' | 'DifferentResource' | 'RestrictAllowedResources'>('SameResource');

  // Load routing material to get initialWeighInGrams
  useEffect(() => {
    if (routingMaterialId) {
      MaterialControllerService.findByUuid(routingMaterialId)
        .then(material => {
          setRoutingMaterial(material);
        })
        .catch(error => {
          console.error('Error loading routing material:', error);
        });
    }
  }, [routingMaterialId]);

  // Update state when value prop changes (like OperationDetailDialog - only when value changes, not when availableOperations changes)
  useEffect(() => {
    if (value) {
      // Find the correct operation values for predecessor and successor
      let predecessorValue = value.predecessorId || '';
      let successorValue = value.successorId || '';
      
      // If predecessorId/successorId are integers, try to find the corresponding operation
      if (typeof value.predecessorId === 'string' && !isNaN(Number(value.predecessorId)) && availableOperations) {
        const predecessorIndex = Number(value.predecessorId);
        const predecessorOp = availableOperations[predecessorIndex];
        if (predecessorOp) {
          predecessorValue = predecessorOp.uuid || String(predecessorIndex);
        }
      }
      
      if (typeof value.successorId === 'string' && !isNaN(Number(value.successorId)) && availableOperations) {
        const successorIndex = Number(value.successorId);
        const successorOp = availableOperations[successorIndex];
        if (successorOp) {
          successorValue = successorOp.uuid || String(successorIndex);
        }
      }
      
      setPredecessorId(predecessorValue);
      setSuccessorId(successorValue);
      setRelationType(value.relationType || 'START_TO_START');
      // Always set to CONSTRAINT (as per specification - only resource constraints are needed)
      setRelationshipCategory('CONSTRAINT');
      
      // Only update minDistance/maxDistance if value actually changed (prevent overwriting user input)
      // Ensure minDistance is a number (convert from string if necessary)
      const minDist = value.minDistance !== undefined && value.minDistance !== null 
        ? (typeof value.minDistance === 'string' ? Number(value.minDistance) : value.minDistance)
        : 0;
      // Only set if different from current state to avoid overwriting user input
      setMinDistance(prev => {
        if (prev !== minDist) {
          return minDist;
        }
        return prev;
      });
      
      // Ensure maxDistance is a number or undefined (convert from string if necessary)
      // IMPORTANT: 0 is a valid value, so we need to check explicitly for undefined/null
      // Use Number.isNaN to handle edge cases
      let maxDist: number | undefined = undefined;
      if (value.maxDistance !== undefined && value.maxDistance !== null) {
        const numValue = typeof value.maxDistance === 'string' ? Number(value.maxDistance) : value.maxDistance;
        // Only set to undefined if it's actually NaN, otherwise keep the value (including 0)
        maxDist = Number.isNaN(numValue) ? undefined : numValue;
      }
      // Only set if different from current state to avoid overwriting user input
      setMaxDistance(prev => {
        if (prev !== maxDist) {
          return maxDist;
        }
        return prev;
      });
      
      // Update resource constraints if they exist
      if (value.resourceConstraints) {
        // Ensure all IDs are strings for consistent comparison
        const predIds = (value.resourceConstraints.predecessorResourceIds || []).map(id => String(id));
        const succIds = (value.resourceConstraints.successorResourceIds || []).map(id => String(id));
        setPredecessorResourceIds(predIds);
        setSuccessorResourceIds(succIds);
        // Map old backend values to new frontend values
        const oldType = value.resourceConstraints.constraintType;
        let newType: 'SameResource' | 'DifferentResource' | 'RestrictAllowedResources' = 'SameResource';
        // Support both new and legacy enum values for backwards compatibility
        const oldTypeStr = oldType as string;
        if (oldTypeStr === 'SAME_RESOURCE' || oldTypeStr === 'RESOURCE_SHARED') {
          newType = 'SameResource';
        } else if (oldTypeStr === 'DIFFERENT_RESOURCES' || oldTypeStr === 'RESOURCE_EXCLUSIVE') {
          newType = 'DifferentResource';
        } else if (oldTypeStr === 'CONSTRAINED_RESOURCES' || oldTypeStr === 'RESOURCE_DEPENDENT') {
          newType = 'RestrictAllowedResources';
        }
        setConstraintType(newType);
      } else {
        // Reset constraints if they don't exist
        setPredecessorResourceIds([]);
        setSuccessorResourceIds([]);
        setConstraintType('SameResource');
      }
    } else {
      // Reset all values if value is null/undefined
      setPredecessorResourceIds([]);
      setSuccessorResourceIds([]);
      setConstraintType('SameResource');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- availableOperations intentionally excluded; only update when value prop changes
  }, [value]);

  // Validation function
  const validateForm = () => {
    const errors: {[key: string]: boolean} = {};
    
    // Check if predecessor and successor are different
    if (predecessorId === successorId) {
      errors.predecessor = true;
      errors.successor = true;
    }
    
    // Check if predecessor is selected
    if (!predecessorId) {
      errors.predecessor = true;
    }
    
    // Check if successor is selected
    if (!successorId) {
      errors.successor = true;
    }
    
    // Check for unique combination (this would need to be passed from parent)
    // For now, we'll skip this validation as it requires access to existing relationships
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = React.useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    // Commit duration values before validation and save
    if (minDistanceRef.current) {
      minDistanceRef.current.commit();
    }
    if (maxDistanceRef.current) {
      maxDistanceRef.current.commit();
    }
    
    if (!validateForm()) {
      setIsSaving(false);
      return;
    }

    // Map new frontend constraint types to backend types
    let backendConstraintType: 'CONSTRAINED_RESOURCES' | 'DIFFERENT_RESOURCES' | 'SAME_RESOURCE' = 'SAME_RESOURCE';
    if (constraintType === 'SameResource') {
      backendConstraintType = 'SAME_RESOURCE';
    } else if (constraintType === 'DifferentResource') {
      backendConstraintType = 'DIFFERENT_RESOURCES';
    } else if (constraintType === 'RestrictAllowedResources') {
      backendConstraintType = 'CONSTRAINED_RESOURCES';
    }

    // minDistance and maxDistance are not needed for CONSTRAINT relationships
    // relationType is not needed for CONSTRAINT relationships

    const relationship: OperationRelationshipDto = {
      uuid: value?.uuid,
      routingId: value?.routingId,
      predecessorId,
      successorId,
      relationshipCategory: 'CONSTRAINT', // Always CONSTRAINT as per specification
      minDistance: undefined, // Not needed for CONSTRAINT relationships
      maxDistance: undefined, // Not needed for CONSTRAINT relationships
      resourceConstraints: { // Always show resource constraints (relationshipCategory is always CONSTRAINT)
        predecessorResourceIds: constraintType === 'RestrictAllowedResources' ? predecessorResourceIds : undefined,
        successorResourceIds: constraintType === 'RestrictAllowedResources' ? successorResourceIds : undefined,
        constraintType: backendConstraintType
      }
    };

    try {
      await Promise.resolve(onSave(relationship));
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validateForm is a local function; including it would trigger on every render
  }, [minDistance, maxDistance, predecessorId, successorId, relationType, relationshipCategory, constraintType, predecessorResourceIds, successorResourceIds, value, onSave, isSaving]);

  // Memoize footerTemplate to ensure it's stable and renders correctly
  // Use inline function like OperationDetailDialog to ensure it renders immediately
  const footerTemplate = () => (
    <div className="flex justify-end gap-2 p-2">
      <ButtonComponent 
        onClick={onClose} 
        cssClass="e-outline e-btn e-outline"
        type="button"
      >
        {t('actions.cancel')}
      </ButtonComponent>
      <ButtonComponent 
        onClick={handleSave} 
        cssClass="e-primary e-btn e-primary"
        type="button"
        disabled={isSaving}
      >
        {isSaving ? t('actions.saving') : t('actions.ok')}
      </ButtonComponent>
    </div>
  );

  // Note: relationTypeOptions and relationshipCategoryOptions removed - only CONSTRAINT category is used

  // Note: Resource constraints initialization is now handled in the main useEffect above
  // This duplicate useEffect has been removed to avoid conflicts

  // Constraint type options
  const constraintTypeOptions = useMemo(() => [
    { text: t('routing.form.constraintTypes.sameResource'), value: 'SameResource' },
    { text: t('routing.form.constraintTypes.differentResource'), value: 'DifferentResource' },
    { text: t('routing.form.constraintTypes.restrictAllowedResources'), value: 'RestrictAllowedResources' }
  ], [t]);

  const operationOptions = availableOperations.map((op, index) => ({
    text: `${op.name || 'Unnamed'} (${index + 1})`,
    value: op.uuid || String(index) // Use UUID if available, otherwise use index
  }));

  // Extract allowed resources from an operation's operation modes
  const getAllowedResources = (operation: OperationDto) => {
    const resourceMap = new Map<string, { id: string; name: string }>();
    
    if (operation.operationModes) {
      operation.operationModes.forEach(mode => {
        if (mode.resourceId && mode.resourceName) {
          // Use resourceId as the key and value - this should match the UUIDs from resourceConstraints
          const resourceId = String(mode.resourceId);
          resourceMap.set(resourceId, {
            id: resourceId,
            name: mode.resourceName
          });
        }
      });
    }
    
    const resources = Array.from(resourceMap.values());
    return resources;
  };

  // Get allowed resources for predecessor and successor operations
  const predecessorOperation = availableOperations.find(op => 
    op.uuid === predecessorId || 
    String(availableOperations.indexOf(op)) === String(predecessorId)
  );
  
  const successorOperation = availableOperations.find(op => 
    op.uuid === successorId || 
    String(availableOperations.indexOf(op)) === String(successorId)
  );

  const predecessorAllowedResources = useMemo(() => predecessorOperation ? getAllowedResources(predecessorOperation) : [], [predecessorOperation]);
  const successorAllowedResources = useMemo(() => successorOperation ? getAllowedResources(successorOperation) : [], [successorOperation]);

  // Dialog body, rendered as children of the DialogComponent below
  const dialogContent = React.useCallback(() => {
    return (
      <div className="p-6 space-y-6" style={{ maxHeight: 'calc(900px - 120px)', overflowY: 'auto' }}>
        {/* Step 1: Operation Selection */}
        <div>
          <h4 className="text-lg font-semibold mb-4">{t('routing.form.operationSelection')}</h4>
          <div className="grid grid-cols-2 gap-4">
            {/* Predecessor Operation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('routing.form.predecessor')} *
              </label>
              <DropDownListComponent
                dataSource={operationOptions}
                fields={{ text: 'text', value: 'value' }}
                value={predecessorId}
                onChange={(e: any) => setPredecessorId(e.value)}
                cssClass={validationErrors.predecessor ? 'e-error' : ''}
                placeholder={t('routing.form.selectPredecessor')}
              />
              {validationErrors.predecessor && (
                <div className="text-red-500 text-sm mt-1">
                  {t('routing.form.predecessor')} {t('form.required')}
                </div>
              )}
            </div>

            {/* Successor Operation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('routing.form.successor')} *
              </label>
              <DropDownListComponent
                dataSource={operationOptions}
                fields={{ text: 'text', value: 'value' }}
                value={successorId}
                onChange={(e: any) => setSuccessorId(e.value)}
                cssClass={validationErrors.successor ? 'e-error' : ''}
                placeholder={t('routing.form.selectSuccessor')}
              />
              {validationErrors.successor && (
                <div className="text-red-500 text-sm mt-1">
                  {t('routing.form.successor')} {t('form.required')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: Relationship Category */}
        <div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              {/* Relationship Category is always CONSTRAINT - hidden as per specification */}
              <input type="hidden" value="CONSTRAINT" />
            </div>
          </div>
        </div>

        {/* Relation Type section intentionally omitted for CONSTRAINT relationships */}

        {/* Step 4: Distance Configuration - Only for TIME relationships */}
        {relationshipCategory === 'TIME' && (
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('routing.form.distanceConfiguration')}</h4>
            <div className="grid grid-cols-2 gap-4">
              {/* Min Distance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('routing.form.minDistance')}
                </label>
                <DurationInput
                  value={minDistance}
                  allowFractionalSeconds={true}
                  onChange={(value) => {
                    setMinDistance(value || 0);
                  }}
                  required={true}
                  onRef={(ref) => {
                    minDistanceRef.current = ref;
                  }}
                />
                {validationErrors.minDistance && (
                  <div className="text-red-500 text-sm mt-1">
                    {t('routing.form.minDistance')} {t('validation.min', { min: 0 })}
                  </div>
                )}
              </div>

              {/* Max Distance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('routing.form.maxDistance')}
                </label>
                <DurationInput
                  value={maxDistance}
                  onChange={(value) => {
                    setMaxDistance(value !== undefined && value !== null ? value : undefined);
                  }}
                  required={false}
                  onRef={(ref) => {
                    maxDistanceRef.current = ref;
                  }}
                />
                {validationErrors.maxDistance && (
                  <div className="text-red-500 text-sm mt-1">
                    {t('routing.form.maxDistance')} {t('validation.min', { min: 0 })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Resource Constraints - Only show for CONSTRAINT relationships */}
        {relationshipCategory === 'CONSTRAINT' && (
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('routing.form.resourceConstraints')}</h4>
            
            {/* Constraint Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('routing.form.constraintType')} *
              </label>
              <DropDownListComponent
                dataSource={constraintTypeOptions}
                fields={{ text: 'text', value: 'value' }}
                value={constraintType}
                onChange={(e: any) => setConstraintType(e.value)}
                placeholder={t('routing.form.selectConstraintType')}
              />
            </div>

            {/* Resource Selection Grids - Side by Side - Only show for RestrictAllowedResources */}
            {constraintType === 'RestrictAllowedResources' && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Predecessor Resources Grid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('routing.form.predecessorResources')}
                  </label>
                  <div className="border rounded-lg" style={{ height: '200px', overflowY: 'auto' }}>
                    {predecessorAllowedResources.length > 0 ? (
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('routing.form.select')}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('routing.form.resourceName')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {predecessorAllowedResources.map((resource) => {
                            const resourceIdStr = String(resource.id);
                            const isChecked = predecessorResourceIds.some(id => String(id) === resourceIdStr);
                            return (
                              <tr key={resource.id}>
                                <td className="px-4 py-2">
                                  <CheckBoxComponent
                                    checked={isChecked}
                                    change={(e: any) => {
                                      if (e.checked) {
                                        setPredecessorResourceIds(prev => [...prev, resourceIdStr]);
                                      } else {
                                        setPredecessorResourceIds(prev => prev.filter(id => String(id) !== resourceIdStr));
                                      }
                                    }}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {resource.name}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-gray-500 text-sm italic p-4">
                        {t('routing.form.noResourcesAvailable')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Successor Resources Grid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('routing.form.successorResources')}
                  </label>
                  <div className="border rounded-lg" style={{ height: '200px', overflowY: 'auto' }}>
                    {successorAllowedResources.length > 0 ? (
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('routing.form.select')}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('routing.form.resourceName')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {successorAllowedResources.map((resource) => {
                            const resourceIdStr = String(resource.id);
                            const isChecked = successorResourceIds.some(id => String(id) === resourceIdStr);
                            return (
                              <tr key={resource.id}>
                                <td className="px-4 py-2">
                                  <CheckBoxComponent
                                    checked={isChecked}
                                    change={(e: any) => {
                                      if (e.checked) {
                                        setSuccessorResourceIds(prev => [...prev, resourceIdStr]);
                                      } else {
                                        setSuccessorResourceIds(prev => prev.filter(id => String(id) !== resourceIdStr));
                                      }
                                    }}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {resource.name}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-gray-500 text-sm italic p-4">
                        {t('routing.form.noResourcesAvailable')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Constraint Description - Simplified */}
            <div className="text-sm text-gray-600 italic">
              {constraintType === 'SameResource' && t('routing.form.constraintDescription.sameResource')}
              {constraintType === 'DifferentResource' && t('routing.form.constraintDescription.differentResource')}
              {constraintType === 'RestrictAllowedResources' && t('routing.form.constraintDescription.restrictAllowedResources')}
            </div>
          </div>
        )}
      </div>
    );
  }, [predecessorId, successorId, relationshipCategory, minDistance, maxDistance, validationErrors, constraintTypeOptions, operationOptions, predecessorAllowedResources, successorAllowedResources, predecessorResourceIds, successorResourceIds, constraintType, t]);

  return (
    <DialogComponent
      isModal={true}
      target="body"
      animationSettings={{ effect: 'None' }}
      cssClass="operation-relationship-detail-dialog"
      zIndex={10000}
      width="1200px"
      height="900px"
      visible={true}
      showCloseIcon={true}
      closeOnEscape={true}
      close={onClose}
      header={value ? t('routing.form.editOperationRelationship') : t('routing.form.addOperationRelationship')}
      footerTemplate={footerTemplate}
      statelessTemplates={[]}
      enableRtl={false}
    >
      {/* Render as children, not as the `content` template: a new template function after
          each state change makes Syncfusion rebuild the body, which resets every scroll position. */}
      {dialogContent()}
    </DialogComponent>
  );
};

export default OperationRelationshipDetailDialog;
